import { useState, useEffect, useMemo } from "react";
import { useTranslation } from "react-i18next";
import ShelfSummaryTile from "../../../components/core/blocks/ShelfSummaryTile";
import ShelfModal from "../../../components/custom/analytics/ShelfModal";
import ShelfDetails from "../../../components/custom/analytics/ShelfDetails";
import DataTable from "../../../components/custom/analytics/DataTable";
import ShareView from "../../../components/custom/analytics/ShareView";
import { useFetch } from "../../../hooks/api";
import { useQueryClient } from "@tanstack/react-query";
import PricingView from "../../../components/custom/analytics/PricingView";
import ProductRanksView from "../../../components/custom/analytics/ProductRanksView";
import moment from "moment";
import PaymentDiscovery from "../../../views/billing/PaymentDiscovery";
import { useAppSelector } from "../../../redux/hooks";
import { css } from "../../../../styled-system/css";
import { HStack, VStack, Wrap } from "../../../../styled-system/jsx";
import { SearchInput } from "../../../components/fields/base/TextField";
import Loading from "../../../components/core/blocks/Loading";
import Card from "../../../components/core/basic/Card";
import { Tabs, TabList, Tab, TabPanel } from "../../../components/core/basic/Tabs";
import { useSimpleCan } from "../../../config/casl/casl";

const Analytics = ({ categoryFilter, clearRedirect, marketplace, setCategoryFilter, shelfRedirect }) => {
  const { t } = useTranslation(["advertising"]);
  const user = useAppSelector((state) => state.user);

  const [selectedShelf, setSelectedShelf] = useState(null);
  const [shelfData, setShelfData] = useState([]);
  const [brandData, setBrandData] = useState([]);
  const [shelfStatsData, setShelfStatsData] = useState([]);
  const [searchFilter, setSearchFilter] = useState("");
  const [filteredShelfData, setFilteredShelfData] = useState([]);
  const [numOfShelfCredits, setNumOfShelfCredits] = useState(0);
  const [numOfShelves, setNumOfShelves] = useState(0);
  const [selectedSearchId, setSelectedSearchId] = useState(null);
  const [selectedRollupType, setSelectedRollupType] = useState("page_1");
  const [dateRange, setSelectedDateRange] = useState(() => [moment().subtract(31, "days"), moment()]);
  const [selectedTab, setSelectedTab] = useState("details");
  const [start, end] = dateRange;

  const settings = user?.modules?.find((m) => m.name === "SEARCH_ANALYTICS")?.settings ?? {};
  const userCanCreateDigitalShelf = useSimpleCan("create", "DigitalShelf");

  const queryClient = useQueryClient();

  const onShelfClick = (id) => {
    clearRedirect(null);
    setSelectedSearchId(null);
    setSelectedShelf(id);
  };

  // Get subscription details - cost per shelf and number of shelves subscribed for
  const { error: subscriptionDetailsError, data: subscriptionDetails } = useFetch(
    ["subscription"],
    "/api/billing/digitalshelf",
    {},
    { enabled: true },
  );

  // Get total number of shelves across account
  const {
    error: shelfCountError,
    data: shelfCountDetails,
    loading: isShelfCountLoading,
    refetch: refetchShelfCount,
  } = useFetch(["shelf_count"], "/api/shelves/", {});

  const {
    error: shelvesError,
    data: shelves,
    isLoading,
    refetch,
  } = useFetch(["shelves", categoryFilter, marketplace], "/api/shelves/", {
    category: categoryFilter,
    marketplace: marketplace,
  });

  const { error: brandsError, data: brands } = useFetch(["brands", marketplace], "/api/brands/", {
    marketplace: marketplace,
  });

  const {
    error: shelfStatsError,
    isLoading: shelfStatsLoading,
    data: shelfStats,
  } = useFetch(
    [
      "shelf_stats",
      selectedShelf,
      selectedSearchId,
      selectedRollupType,
      marketplace,
      start.format("YYYY-MM-DD"),
      end.format("YYYY-MM-DD"),
    ],
    `/api/shelf_stats/${selectedShelf}`,
    {
      search_id: selectedSearchId,
      rollup_type: selectedRollupType,
      start: start.format("YYYY-MM-DD"),
      end: end.format("YYYY-MM-DD"),
    },
    {
      enabled: !!selectedShelf,
    },
  );

  const {
    error: shelfStatsTotalError,
    isLoading: shelfStatsTotalLoading,
    data: shelfStatsTotalData,
  } = useFetch(
    [
      "shelf_stats_total",
      selectedShelf,
      selectedSearchId,
      selectedRollupType,
      marketplace,
      start.format("YYYY-MM-DD"),
      end.format("YYYY-MM-DD"),
    ],
    `/api/shelf_stats/${selectedShelf}`,
    {
      search_id: selectedSearchId,
      rollup_type: selectedRollupType,
      group: "total",
      start: start.format("YYYY-MM-DD"),
      end: end.format("YYYY-MM-DD"),
    },
    {
      enabled: !!selectedShelf,
      select: (d) => d?.data || [],
      initialData: () => ({ data: [] }),
    },
  );
  const isStatsLoading = shelfStatsLoading || shelfStatsTotalLoading;

  // Retrieve updated data from the API upon shelf deletion or update so it is accurately displayed in the UI without a reload
  const updateShelfData = (type = "update", id = null) => {
    refetch();

    if (shelves?.data.results) {
      setShelfData(shelves.data.results);
    }

    // When shelf is deleted, will auto-select first available shelf, or display creation form in carousel if no other shelves exist
    if (type === "delete") {
      setSelectedShelf(null);

      // Update shelf count after deletion
      queryClient.invalidateQueries({
        queryKey: ["count"],
      });
      refetchShelfCount();
    }

    // Upon successful creation, set selected shelf to new ID so user can see their newly added shelf
    if (type === "create" && id !== null && !isLoading && !isShelfCountLoading) {
      setSelectedShelf(id);

      // Update shelf count after creation
      queryClient.invalidateQueries({
        queryKey: ["count"],
      });
      refetchShelfCount();
    }
  };

  useEffect(() => {
    if (shelves?.data.results) {
      setShelfData(shelves.data.results);
      if (searchFilter.length < 1) {
        setFilteredShelfData(shelves.data.results);
      }
    }

    if (brands?.data) {
      setBrandData(brands.data);
    }

    if (shelfStats?.data) {
      setShelfStatsData(shelfStats.data);
    }

    if (subscriptionDetails) {
      setNumOfShelfCredits(subscriptionDetails.data.count);
    }

    if (shelfCountDetails) {
      setNumOfShelves(shelfCountDetails.data.count);
    }

    if (shelfRedirect) {
      setSelectedShelf(shelfRedirect);
    }
  }, [
    shelves,
    brands,
    brandData,
    shelfStats,
    shelvesError,
    brandsError,
    shelfStatsError,
    selectedShelf,
    shelfStatsData,
    filteredShelfData,
    searchFilter.length,
    subscriptionDetails,
    subscriptionDetailsError,
    shelfCountDetails,
    shelfCountError,
    shelfStatsTotalError,
    marketplace,
    categoryFilter,
    shelfRedirect,
  ]);

  if (selectedShelf === null && shelfData.length > 0) {
    setSelectedShelf(shelfData[0].id);
  }

  useEffect(() => {
    setSelectedShelf(shelfData[0]?.id ?? null);
  }, [categoryFilter, shelfData]);

  const filterShelves = (filterValue) => {
    setSearchFilter(filterValue);
    const filteredData = shelfData.filter(
      (shelf) => shelf?.name?.toLowerCase().indexOf(filterValue.toLowerCase()) > -1,
    );
    setFilteredShelfData(filteredData);
  };

  const homeBrand = useMemo(() => {
    if ((selectedShelf, shelfData)) {
      const shelf = shelfData.find((shelf) => shelf.id === selectedShelf);
      return shelf?.brand_name ?? "";
    }
    return "";
  }, [shelfData, selectedShelf]);

  const tabs = {
    details: (
      <ShelfDetails
        key={selectedShelf}
        data={shelfData}
        brandData={brandData}
        selectedShelf={selectedShelf}
        categoryFilter={categoryFilter}
        updateShelfData={updateShelfData}
      />
    ),
    products: (
      <ProductRanksView
        shelfStatsData={shelfStatsData}
        homeBrand={homeBrand}
        selectedShelf={selectedShelf}
        filterSearchId={setSelectedSearchId}
        shelfData={shelfData}
        selectedSearchId={selectedSearchId}
        filterRollupType={setSelectedRollupType}
        selectedRollupType={selectedRollupType}
        setSelectedDateRange={setSelectedDateRange}
        marketPlace={marketplace}
        dateRange={dateRange}
        isLoading={isStatsLoading}
      />
    ),
    share: (
      <ShareView
        shelfStatsData={shelfStatsData}
        homeBrand={homeBrand}
        selectedShelf={selectedShelf}
        filterSearchId={setSelectedSearchId}
        shelfData={shelfData}
        selectedSearchId={selectedSearchId}
        filterRollupType={setSelectedRollupType}
        selectedRollupType={selectedRollupType}
        shelfStatsTotalData={shelfStatsTotalData}
        setSelectedDateRange={setSelectedDateRange}
        dateRange={dateRange}
        isLoading={isStatsLoading}
      />
    ),
    pricing: (
      <PricingView
        homeBrand={homeBrand}
        selectedShelf={selectedShelf}
        filterSearchId={setSelectedSearchId}
        shelfData={shelfData}
        selectedSearchId={selectedSearchId}
        filterRollupType={setSelectedRollupType}
        setSelectedDateRange={setSelectedDateRange}
        shelfStatsTotalData={shelfStatsTotalData}
        selectedRollupType={selectedRollupType}
        dateRange={dateRange}
        isLoading={isStatsLoading}
      />
    ),
    brands: (
      <DataTable
        settings={settings}
        homeBrand={homeBrand}
        shelfStatsData={shelfStatsData}
        selectedShelf={selectedShelf}
        filterSearchId={setSelectedSearchId}
        shelfData={shelfData}
        selectedSearchId={selectedSearchId}
        filterRollupType={setSelectedRollupType}
        selectedRollupType={selectedRollupType}
        shelfStatsTotalData={shelfStatsTotalData}
        setSelectedDateRange={setSelectedDateRange}
        dateRange={dateRange}
        isLoading={isStatsLoading}
      />
    ),
  };

  if (!user.analytics_tier || ["unknown", "MANUALLY_DISABLED"].includes(user.analytics_tier)) {
    return (
      <div style={{ marginTop: "30px" }}>
        <PaymentDiscovery discoveryType={"search-analytics"} />
      </div>
    );
  }

  return (
    <>
      <HStack justify={"space-between"}>
        <p className={css({ margin: 0, color: "text.secondary", fontWeight: "bold", fontSize: "xlarge" })}>
          {t(($) => $.advertising["Select one of the shelves below to view analytics"])}
        </p>
        {userCanCreateDigitalShelf && (
          <ShelfModal
            categoryFilter={categoryFilter}
            data={shelfData.find((shelf) => shelf.id === selectedShelf)}
            brandData={brandData}
            updateShelfData={updateShelfData}
            numOfShelfCredits={numOfShelfCredits}
            numOfShelves={numOfShelves}
            setCategoryFilter={setCategoryFilter}
          />
        )}
      </HStack>
      <Wrap>
        <VStack flex={1} alignItems="stretch" minWidth={"350px"} maxWidth={"25%"}>
          <SearchInput
            aria-label={t(($) => $.advertising["Shelf search"])}
            placeholder={t(($) => $.advertising["Search for a shelf..."])}
            onChange={filterShelves}
            value={searchFilter}
            css={{ flex: 0 }}
          />

          {filteredShelfData.map((shelf) => (
            <ShelfSummaryTile
              key={shelf.id}
              selected={shelf.id === selectedShelf}
              shelf={shelf}
              onClick={() => onShelfClick(shelf.id)}
            />
          ))}
        </VStack>
        <VStack flex={1} minWidth={"500px"}>
          {isLoading ?
            <Loading />
          : (
            shelfData.length < 1 ||
            (categoryFilter && shelfData.filter((s) => s.category === categoryFilter)?.length < 1)
          ) ?
            <Card width="100%">
              <h4
                className={css({
                  fontSize: "large",
                  fontWeight: "bold",
                  margin: 0,
                  color: "text.secondary",
                })}
              >
                {t(($) => $.advertising["Create a New Shelf"])}
              </h4>
              <ShelfDetails
                data={shelfData}
                brandData={brandData}
                selectedShelf={selectedShelf}
                categoryFilter={categoryFilter}
                updateShelfData={updateShelfData}
                numOfShelfCredits={numOfShelfCredits}
                numOfShelves={numOfShelves}
                fullLayout
                setCategoryFilter={setCategoryFilter}
              />
            </Card>
          : <Card width="100%">
              <Tabs selectedKey={selectedTab} onSelectionChange={setSelectedTab}>
                <TabList>
                  <Tab id="details">{t(($) => $.advertising["Details"])}</Tab>
                  <Tab id="products">{t(($) => $.advertising["Products"])}</Tab>
                  <Tab id="share">{t(($) => $.advertising["Share"])}</Tab>
                  <Tab id="pricing">{t(($) => $.advertising["Pricing"])}</Tab>
                  <Tab id="brands">{t(($) => $.advertising["Brands"])}</Tab>
                </TabList>
                <TabPanel id="details">{tabs.details}</TabPanel>
                <TabPanel id="products">{tabs.products}</TabPanel>
                <TabPanel id="share">{tabs.share}</TabPanel>
                <TabPanel id="pricing">{tabs.pricing}</TabPanel>
                <TabPanel id="brands">{tabs.brands}</TabPanel>
              </Tabs>
            </Card>
          }
        </VStack>
      </Wrap>
    </>
  );
};

export default Analytics;
